import ClearIcon from '@mui/icons-material/Clear'
import SearchIcon from '@mui/icons-material/Search'
import EditIcon from '@mui/icons-material/EditOutlined'
import PersonSearchIcon from '@mui/icons-material/PersonSearchOutlined'
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Toolbar,
  Tooltip,
  Typography
} from '@mui/material'
import { format } from 'date-fns'
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { selectAuthUser } from '../auth/AuthSlice'
import {
  AdminUser as AdminUserType,
  fetchAllAdminUsersAsync, fetchAllUserRolesAsync, saveAdminUserAsync, searchAdminUsersAsync,
  selectAdminUserStatus, selectAllAdminUsers, selectAllUserRoles
} from './AdminUserSlice'

const toEpochDay = (epochSec: number) => epochSec ? format(new Date(epochSec * 1000), 'yyyy-MM-dd') : '—'
const isExpired = (epochSec: number) => epochSec && epochSec * 1000 < Date.now()

type BulkAction = 'setCredits' | 'adjustCredits' | 'setExpiry' | 'addRole' | 'removeRole'

const AdminUser = () => {
  const dispatch = useAppDispatch()
  const adminUsers = useAppSelector(selectAllAdminUsers)
  const allRoles = useAppSelector(selectAllUserRoles)
  const status = useAppSelector(selectAdminUserStatus)
  const authUser = useAppSelector(selectAuthUser)
  const userToAssume = localStorage.getItem('userToAssume')
  const navigate = useNavigate()

  const [page, setPage] = useState(0)
  const [size, setSize] = useState(25)                 // was 1000 — the reason the page crawled
  const [sort, setSort] = useState('userEmail,asc')
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')

  // client-side refinements applied on top of the loaded page
  const [roleFilter, setRoleFilter] = useState('')
  const [minCredits, setMinCredits] = useState('')
  const [maxCredits, setMaxCredits] = useState('')
  const [preset, setPreset] = useState<'all' | 'negative' | 'expired' | 'noUser'>('all')

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [bulkOpen, setBulkOpen] = useState(false)
  const [bulkAction, setBulkAction] = useState<BulkAction>('setCredits')
  const [bulkValue, setBulkValue] = useState('')
  const [bulkRole, setBulkRole] = useState('')
  const [applying, setApplying] = useState<{ done: number, total: number } | null>(null)

  const [editRow, setEditRow] = useState<AdminUserType | null>(null)
  const [editCredits, setEditCredits] = useState('')
  const [editExpiry, setEditExpiry] = useState('')
  const [editRoles, setEditRoles] = useState('')

  const [snack, setSnack] = useState<{ msg: string, sev: 'success' | 'error' | 'info' } | null>(null)

  useEffect(() => {
    const h = setTimeout(() => setDebouncedSearchQuery(searchQuery), 350)   // was 1000ms
    return () => clearTimeout(h)
  }, [searchQuery])

  useEffect(() => {
    if (debouncedSearchQuery) {
      dispatch(searchAdminUsersAsync({ query: debouncedSearchQuery, page: { page, size, sort } }))
    } else {
      dispatch(fetchAllAdminUsersAsync({ mode: 'regular', page: { page, size, sort } }))
    }
    setSelected(new Set())
  }, [debouncedSearchQuery, page, size, sort])

  useEffect(() => { dispatch(fetchAllUserRolesAsync({ mode: 'regular' })) }, [])

  const isLoading = status === 'loading'

  const handleSort = (col: string) => setSort(sort === `${col},asc` ? `${col},desc` : `${col},asc`)

  const rows: AdminUserType[] = adminUsers.content ?? []

  // client-side filtering over the loaded page
  const filtered = useMemo(() => rows.filter((r) => {
    if (roleFilter && !(r.role || '').includes(roleFilter)) return false
    const c = Number(r.paidTierCount)
    if (minCredits !== '' && c < Number(minCredits)) return false
    if (maxCredits !== '' && c > Number(maxCredits)) return false
    if (preset === 'negative' && !(c < 0)) return false
    if (preset === 'expired' && !isExpired(r.paidTierExpiry)) return false
    if (preset === 'noUser' && (r.role || '').includes('ROLE_USER')) return false
    return true
  }), [rows, roleFilter, minCredits, maxCredits, preset])

  const hasClientFilter = !!roleFilter || minCredits !== '' || maxCredits !== '' || preset !== 'all'
  const clearFilters = () => { setRoleFilter(''); setMinCredits(''); setMaxCredits(''); setPreset('all') }

  const allSelectedOnPage = filtered.length > 0 && filtered.every((r) => selected.has(r.userId))
  const someSelected = filtered.some((r) => selected.has(r.userId))
  const toggleAll = () => {
    const next = new Set(selected)
    if (allSelectedOnPage) filtered.forEach((r) => next.delete(r.userId))
    else filtered.forEach((r) => next.add(r.userId))
    setSelected(next)
  }
  const toggleOne = (id: string) => {
    const next = new Set(selected)
    next.has(id) ? next.delete(id) : next.add(id)
    setSelected(next)
  }

  const selectedUsers = rows.filter((r) => selected.has(r.userId))

  const buildUpdate = (u: AdminUserType): AdminUserType | null => {
    const roles = (u.role || '').split(',').map((s) => s.trim()).filter(Boolean)
    switch (bulkAction) {
      case 'setCredits':
        return { ...u, paidTierCount: Number(bulkValue) }
      case 'adjustCredits':
        return { ...u, paidTierCount: Number(u.paidTierCount) + Number(bulkValue) }
      case 'setExpiry':
        return { ...u, paidTierExpiry: Math.floor(new Date(bulkValue + 'T00:00:00').getTime() / 1000) }
      case 'addRole':
        return roles.includes(bulkRole) ? null : { ...u, role: [...roles, bulkRole].join(',') }
      case 'removeRole':
        return roles.includes(bulkRole) ? { ...u, role: roles.filter((r) => r !== bulkRole).join(',') || 'ROLE_ANON' } : null
      default: return null
    }
  }

  const applyBulk = async () => {
    const updates = selectedUsers.map(buildUpdate).filter(Boolean) as AdminUserType[]
    if (!updates.length) { setSnack({ msg: 'Nothing to change for the current selection.', sev: 'info' }); return }
    setApplying({ done: 0, total: updates.length })
    let ok = 0, fail = 0
    for (const u of updates) {
      try { await (dispatch(saveAdminUserAsync({ adminUser: u, mode: 'regular' })) as any).unwrap(); ok++ }
      catch { fail++ }
      setApplying((p) => p ? { ...p, done: p.done + 1 } : p)
    }
    setApplying(null)
    setBulkOpen(false)
    setSelected(new Set())
    setSnack({ msg: `Bulk update: ${ok} succeeded${fail ? `, ${fail} failed` : ''}.`, sev: fail ? 'error' : 'success' })
    dispatch(fetchAllAdminUsersAsync({ mode: 'regular', page: { page, size, sort } }))
  }

  const openEdit = (u: AdminUserType) => {
    setEditRow(u)
    setEditCredits(String(u.paidTierCount ?? ''))
    setEditExpiry(u.paidTierExpiry ? format(new Date(u.paidTierExpiry * 1000), 'yyyy-MM-dd') : '')
    setEditRoles((u.role || '').split(',').map((s) => s.trim()).filter(Boolean).join(', '))
  }
  const saveEdit = async () => {
    if (!editRow) return
    const roles = editRoles.split(',').map((s) => s.trim()).filter(Boolean)
    const updated: AdminUserType = {
      ...editRow,
      paidTierCount: Number(editCredits),
      paidTierExpiry: editExpiry ? Math.floor(new Date(editExpiry + 'T00:00:00').getTime() / 1000) : editRow.paidTierExpiry,
      role: roles.length ? roles.join(',') : 'ROLE_ANON'
    }
    try {
      await (dispatch(saveAdminUserAsync({ adminUser: updated, mode: 'regular' })) as any).unwrap()
      setSnack({ msg: `Saved ${editRow.userEmail}.`, sev: 'success' })
      dispatch(fetchAllAdminUsersAsync({ mode: 'regular', page: { page, size, sort } }))
    } catch { setSnack({ msg: `Failed to save ${editRow.userEmail}.`, sev: 'error' }) }
    setEditRow(null)
  }

  const bulkNeedsValue = bulkAction === 'setCredits' || bulkAction === 'adjustCredits' || bulkAction === 'setExpiry'
  const bulkNeedsRole = bulkAction === 'addRole' || bulkAction === 'removeRole'

  return (<Box sx={{ maxWidth: 1200, mx: 'auto' }}>
    <Backdrop sx={{ color: '#fff', zIndex: (t) => t.zIndex.drawer + 1 }} open={isLoading && !applying}>
      <CircularProgress color="inherit" />
    </Backdrop>

    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>Users</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      Search, filter, and bulk-edit credits, expirations, and roles.
    </Typography>

    {/* Filter bar */}
    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
      <TextField
        placeholder="Search email, domain (e.g. @purepm.co), or user ID"
        value={searchQuery}
        onChange={(e) => { setSearchQuery(e.target.value); setPage(0) }}
        fullWidth size="small"
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
          endAdornment: searchQuery ? <InputAdornment position="end"><IconButton size="small" onClick={() => { setSearchQuery(''); setPage(0) }}><ClearIcon fontSize="small" /></IconButton></InputAdornment> : undefined
        }}
      />
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mt: 1.5 }} alignItems={{ md: 'center' }} flexWrap="wrap" useFlexGap>
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Role</InputLabel>
          <Select label="Role" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
            <MenuItem value="">Any role</MenuItem>
            {allRoles?.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
          </Select>
        </FormControl>
        <TextField size="small" type="number" label="Min credits" value={minCredits} onChange={(e) => setMinCredits(e.target.value)} sx={{ width: 120 }} />
        <TextField size="small" type="number" label="Max credits" value={maxCredits} onChange={(e) => setMaxCredits(e.target.value)} sx={{ width: 120 }} />
        <Stack direction="row" spacing={1}>
          {([['negative', 'Negative credits'], ['expired', 'Expired'], ['noUser', 'No ROLE_USER']] as const).map(([key, label]) =>
            <Chip key={key} label={label} size="small" color={preset === key ? 'primary' : 'default'} variant={preset === key ? 'filled' : 'outlined'}
              onClick={() => setPreset(preset === key ? 'all' : key)} />)}
        </Stack>
        {hasClientFilter && <Button size="small" startIcon={<ClearIcon />} onClick={clearFilters} sx={{ textTransform: 'none' }}>Clear filters</Button>}
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {hasClientFilter ? `${filtered.length} of ${rows.length} on this page` : `${rows.length} on this page · ${adminUsers.totalElements ?? 0} total`}
        </Typography>
      </Stack>
      {hasClientFilter && rows.length >= size && (
        <Alert severity="info" sx={{ mt: 1.5, py: 0 }}>
          Filtering the loaded page only. To act on <b>every</b> matching user, raise “rows per page” (bottom) so the whole set loads, then select all.
        </Alert>
      )}
    </Paper>

    {/* Bulk action bar */}
    {selected.size > 0 && <Toolbar disableGutters sx={{ px: 2, mb: 1, borderRadius: 2, bgcolor: 'primary.main', color: 'primary.contrastText', minHeight: '52px !important' }}>
      <Typography sx={{ fontWeight: 600 }}>{selected.size} selected</Typography>
      <Button size="small" onClick={() => setSelected(new Set())} sx={{ color: 'inherit', ml: 1, textTransform: 'none' }}>Clear</Button>
      <Box sx={{ flexGrow: 1 }} />
      <Button variant="contained" color="secondary" startIcon={<EditIcon />} onClick={() => setBulkOpen(true)}>Bulk edit</Button>
    </Toolbar>}

    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow sx={{ '& th': { bgcolor: 'action.hover', fontWeight: 600 } }}>
            <TableCell padding="checkbox">
              <Checkbox size="small" checked={allSelectedOnPage} indeterminate={someSelected && !allSelectedOnPage} onChange={toggleAll} />
            </TableCell>
            <TableCell><TableSortLabel active={sort.startsWith('userEmail')} direction={sort.endsWith('asc') ? 'asc' : 'desc'} onClick={() => handleSort('userEmail')}>Email</TableSortLabel></TableCell>
            <TableCell>Roles</TableCell>
            <TableCell align="right"><TableSortLabel active={sort.startsWith('paidTierCount')} direction={sort.endsWith('asc') ? 'asc' : 'desc'} onClick={() => handleSort('paidTierCount')}>Credits</TableSortLabel></TableCell>
            <TableCell><TableSortLabel active={sort.startsWith('paidTierExpiry')} direction={sort.endsWith('asc') ? 'asc' : 'desc'} onClick={() => handleSort('paidTierExpiry')}>Expires</TableSortLabel></TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filtered.length === 0 ? <TableRow><TableCell colSpan={6}>
            <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>No users match.</Typography>
          </TableCell></TableRow> : filtered.map((row) => {
            const sel = selected.has(row.userId)
            const neg = Number(row.paidTierCount) < 0
            const exp = isExpired(row.paidTierExpiry)
            return <TableRow key={row.userId} hover selected={sel}>
              <TableCell padding="checkbox"><Checkbox size="small" checked={sel} onChange={() => toggleOne(row.userId)} /></TableCell>
              <TableCell sx={{ fontWeight: 500 }}>{row.userEmail}</TableCell>
              <TableCell>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {(row.role || '').split(',').map((r) => r.trim()).filter(Boolean).map((r) =>
                    <Chip key={r} label={r.replace('ROLE_', '')} size="small" variant="outlined"
                      color={r === 'ROLE_USER' ? 'success' : r === 'ROLE_ADMIN' ? 'warning' : 'default'} />)}
                </Stack>
              </TableCell>
              <TableCell align="right">
                <Typography variant="body2" sx={{ fontWeight: 600, color: neg ? 'error.main' : 'text.primary' }}>{row.paidTierCount}</Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap', color: exp ? 'error.main' : 'text.secondary' }}>{toEpochDay(row.paidTierExpiry)}</TableCell>
              <TableCell align="right">
                <Tooltip title="Quick edit"><IconButton size="small" color="primary" onClick={() => openEdit(row)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                {userToAssume !== row.userEmail && authUser?.email !== row.userEmail &&
                  <Tooltip title="Assume role"><IconButton size="small" onClick={() => { localStorage.setItem('userToAssume', row.userEmail); window.location.reload() }}><PersonSearchIcon fontSize="small" /></IconButton></Tooltip>}
              </TableCell>
            </TableRow>
          })}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[25, 50, 100, 250, 1000, 5000]}
        component="div"
        count={adminUsers.totalElements || 0}
        rowsPerPage={size}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        onRowsPerPageChange={(e) => { setSize(parseInt(e.target.value, 10)); setPage(0) }}
      />
    </TableContainer>

    {/* Bulk edit dialog */}
    <Dialog open={bulkOpen} onClose={() => !applying && setBulkOpen(false)} fullWidth maxWidth="xs">
      <DialogTitle>Bulk edit {selected.size} user{selected.size === 1 ? '' : 's'}</DialogTitle>
      <DialogContent>
        <FormControl fullWidth size="small" sx={{ mt: 1, mb: 2 }}>
          <InputLabel>Action</InputLabel>
          <Select label="Action" value={bulkAction} onChange={(e) => setBulkAction(e.target.value as BulkAction)}>
            <MenuItem value="setCredits">Set credits to…</MenuItem>
            <MenuItem value="adjustCredits">Add / subtract credits…</MenuItem>
            <MenuItem value="setExpiry">Set expiration date…</MenuItem>
            <MenuItem value="addRole">Add role…</MenuItem>
            <MenuItem value="removeRole">Remove role…</MenuItem>
          </Select>
        </FormControl>
        {bulkAction === 'setCredits' && <TextField autoFocus fullWidth size="small" type="number" label="New credit balance" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} />}
        {bulkAction === 'adjustCredits' && <TextField autoFocus fullWidth size="small" type="number" label="Change (e.g. 30 or -10)" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} helperText="Added to each user's current balance" />}
        {bulkAction === 'setExpiry' && <TextField autoFocus fullWidth size="small" type="date" label="Expires on" InputLabelProps={{ shrink: true }} value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} />}
        {bulkNeedsRole && <FormControl fullWidth size="small"><InputLabel>Role</InputLabel>
          <Select label="Role" value={bulkRole} onChange={(e) => setBulkRole(e.target.value)}>
            {allRoles?.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
          </Select></FormControl>}
        {applying && <Box sx={{ mt: 2 }}><LinearProgress variant="determinate" value={(applying.done / applying.total) * 100} />
          <Typography variant="caption" color="text.secondary">{applying.done} / {applying.total}</Typography></Box>}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setBulkOpen(false)} disabled={!!applying}>Cancel</Button>
        <Button variant="contained" onClick={applyBulk} disabled={!!applying || (bulkNeedsValue && bulkValue === '') || (bulkNeedsRole && !bulkRole)}>
          Apply to {selected.size}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Quick edit dialog */}
    <Dialog open={!!editRow} onClose={() => setEditRow(null)} fullWidth maxWidth="xs">
      <DialogTitle sx={{ pb: 0 }}>Edit user</DialogTitle>
      {editRow && <DialogContent>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{editRow.userEmail}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>{editRow.userId}</Typography>
        <Stack spacing={2}>
          <TextField size="small" type="number" label="Credits" value={editCredits} onChange={(e) => setEditCredits(e.target.value)} />
          <TextField size="small" type="date" label="Expires on" InputLabelProps={{ shrink: true }} value={editExpiry} onChange={(e) => setEditExpiry(e.target.value)} />
          <TextField size="small" label="Roles (comma separated)" value={editRoles} onChange={(e) => setEditRoles(e.target.value)} helperText={`Available: ${(allRoles || []).join(', ')}`} />
        </Stack>
      </DialogContent>}
      <DialogActions>
        <Button onClick={() => setEditRow(null)}>Cancel</Button>
        <Button variant="contained" onClick={saveEdit}>Save</Button>
      </DialogActions>
    </Dialog>

    <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
      {snack ? <Alert severity={snack.sev} onClose={() => setSnack(null)} variant="filled">{snack.msg}</Alert> : undefined}
    </Snackbar>
  </Box>)
}

export default AdminUser
